
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert, warnAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";
import { convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import PayableReceivableCashFlowType from "../../../components/Custom/CashFlow/PayableReceivable/PayableReceivableCashFlowType";
import { getId } from "../../../../utils/ApiUtils";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../../../context/BranchContext";

const PayableReceivableCashFlowNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [resetForm, setResetForm] = useState(false);
    const [entityData, setEntityData] = useState(null);
    const navigate = useNavigate();

    const getIncompatibleCategoryMessage = (type, category) => {
        if (!type || !category?.dreGroup) {
            return null;
        }

        if (type.value === PayableReceivableCashFlowType.RECEIVABLE && category.dreGroup !== 'RECEITA') {
            return t('cash_flow.validation.category.receivable_must_be_revenue');
        }

        if (type.value === PayableReceivableCashFlowType.PAYABLE && category.dreGroup === 'RECEITA') {
            return t('cash_flow.validation.category.payable_cannot_be_revenue');
        }

        return null;
    };

    const getInitialValues = () => ({
        value: 0.00,
        description: '',
        type: null,
        dueDate: null,
        paid: false,
        paymentDate: null,
        paymentMethod: null,
        category: null,
        // INFO(Devs): Mantém a filial selecionada após cadastrar uma conta e limpar o formulário.
        branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null
        , recurrenceEnabled: false, recurrenceCount: 2, recurrenceEvery: 1,
        recurrenceFrequency: { value: 'MONTH', label: 'Mensal' }
    });

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        paid: Yup.boolean().optional(), // Flag para indicar se foi pago
        paymentDate: Yup.string()
            .nullable()
            .when('paid', {
                is: true, // A validação só será aplicada se 'paid' for true
                then: Yup.string().required(t('validation.required.date')).nullable(), // Exige a data de pagamento se pago
                otherwise: Yup.string().nullable(), // Se não for pago, a data de pagamento pode ser nula
            }),
        paymentMethod: Yup.object()
            .nullable()
            .when('paid', {
                is: true, // A validação só será aplicada se 'paid' for true
                then: Yup.object().required(t('validation.required.payment_method')).nullable(), // Exige a data de pagamento se pago
                otherwise: Yup.object().nullable(), // Se não for pago, a data de pagamento pode ser nula
            }),
        dueDate: Yup.string().required(t('validation.required.date')).nullable(),
        value: Yup.number()
            .typeError(t('validation.required.value'))
            .positive(t('validation.required.value'))
            .required(t('validation.required.value')),
        category: Yup.object()
            .nullable()
            .test('dre-group-compatible-with-type', function (category) {
                const message = getIncompatibleCategoryMessage(this.parent.type, category);
                return message ? this.createError({ message }) : true;
            }),
        description: Yup.string().required(t('validation.required.description')),
        type: Yup.object().required(t('validation.required.type')).nullable(),
        branch: Yup.object().required(t('validation.required.branch')).nullable(),
        recurrenceCount: Yup.number().when('recurrenceEnabled', { is: true, then: schema => schema.min(2).required() }),
        recurrenceEvery: Yup.number().when('recurrenceEnabled', { is: true, then: schema => schema.min(1).required() }),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema: formSchema,
        onSubmit: (values) => {

            const categoryValidationMessage = getIncompatibleCategoryMessage(values.type, values.category);
            if (categoryValidationMessage) {
                warnAlert(categoryValidationMessage);
                formik.setFieldError('category', categoryValidationMessage);
                formik.setSubmitting(false);
                return;
            }

            let paid = false;
            let paymentMethod = null;
            let paymentDate = null;

            if (values.paid !== undefined && values.paid) {
                paid = true;
                paymentMethod = values.paymentMethod.value;
                paymentDate = values.paymentDate;
            }

            let payload = {
                id: getId(entityData),
                value: convertStringToStringDouble(values.value),
                branch: {
                    id: values.branch.value
                },
                paymentMethod: paymentMethod,
                description: values.description,
                type: values.type.value === PayableReceivableCashFlowType.PAYABLE ? PayableReceivableCashFlowType.PAYABLE : PayableReceivableCashFlowType.RECEIVABLE,
                paymentDate: paymentDate,
                paid: paid,
                dueDate: values.dueDate,
                category: values.category ? { id: values.category.value } : null
                , recurrenceCount: values.recurrenceEnabled ? Number(values.recurrenceCount) : null,
                recurrenceEvery: values.recurrenceEnabled ? Number(values.recurrenceEvery) : null,
                recurrenceFrequency: values.recurrenceEnabled ? values.recurrenceFrequency?.value : null
            }

            // Aciona a api
            axiosInstance.post(Api.cash_flow.payable_receivable, payload)
                .then(() => {
                    successAlert(t('message.operation_successfully'), () => {
                        if (entityData == null) {
                            onReset();
                        } else {
                            navigate(MenuConfig.cash_flow.payable_receivable.route);
                        }
                    });
                })
                .catch(error => ApiError(error))
                .finally(() => {
                    // Deixa habilitado o botão de submit.
                    formik.setSubmitting(false);
                });
        },
    });

    const onReset = () => {
        // INFO(Devs): resetForm puro limpa a filial; isso quebrava o segundo cadastro no mesmo formulário.
        formik.resetForm({ values: getInitialValues() });

        // Reaplica o contexto de filial no filho depois que o Formik restaura os valores iniciais.
        setResetForm((current) => !current);
    }

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    return (
        <>
            <NewPage
                motherMenu={t('cash_flow.label')}
                childMenu={t('cash_flow.accounts_payable_receivable')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        entityData={entityData}
                        resetForm={resetForm} />}
                path={"/cash-flow/payable-receivable"}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.cash_flow.payable_receivable.route}
                setEntityData={setEntityData}
            />
        </>
    );
}

export default PayableReceivableCashFlowNew;
